/**
 * ProfileHeader Component
 *
 * Displays owner information (username, avatar) with optional "View Profile" button.
 * Used in public scan pages to show item owner details with privacy protection.
 *
 * @privacy The "View Profile" button ONLY renders when onViewProfile prop is provided.
 * This enforces backend privacy rules: public items include userId, private items don't.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Avatar } from "./ui/avatar";
import { Button } from "./ui/button";
import { EyeIcon } from "./icons";

export interface ProfileHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Owner's username (always present) */
  username: string;
  /** Owner's avatar URL (optional) */
  avatar?: string;
  /** Callback for viewing profile (optional - only for public items) */
  onViewProfile?: () => void;
}

/**
 * ProfileHeader - Display owner info with conditional profile navigation
 *
 * Shows username and avatar with an optional "View Profile" button.
 * The button only appears when onViewProfile is provided, protecting user privacy.
 *
 * @example
 * ```tsx
 * // Public item - show View Profile button
 * <ProfileHeader
 *   username="johndoe"
 *   avatar="https://example.com/avatar.jpg"
 *   onViewProfile={() => navigate('/profile/johndoe')}
 * />
 *
 * // Private item - no View Profile button
 * <ProfileHeader
 *   username="johndoe"
 *   avatar="https://example.com/avatar.jpg"
 * />
 * ```
 */
export const ProfileHeader = React.forwardRef<
  HTMLDivElement,
  ProfileHeaderProps
>(({ username, avatar, onViewProfile, className, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className={cn("flex flex-col items-center gap-[1rem] w-full", className)}
      {...props}
    >
      {/* "This item belongs to" label */}
      <p className="text-[0.9375rem] text-gray-700 text-center leading-[1.25rem] font-montserrat">
        {t("publicScan.itemBelongsTo", "This item belongs to")}
      </p>

      {/* Avatar */}
      <Avatar
        src={avatar}
        alt={username}
        size="2xl"
        fallback={
          <span className="text-[1.5rem] text-gray-700 uppercase">
            {username.slice(0, 2)}
          </span>
        }
      />

      {/* Username */}
      <h2 className="text-[0.9375rem] font-bold text-black text-center leading-[1.25rem] font-montserrat">
        {username}
      </h2>

      {/* Conditionally render View Profile button (CRITICAL for privacy) */}
      {onViewProfile && (
        <Button
          variant="primary-dark"
          onClick={onViewProfile}
          type="button"
          leftIcon={<EyeIcon className="size-3" />}
          className="h-[2.5rem] w-full uppercase gap-[1rem]"
          aria-label={t(
            "publicScan.goToProfile",
            "GO TO {{username}} PROFILE",
            { username }
          )}
        >
          {t("publicScan.goToProfile", "GO TO {{username}} PROFILE", {
            username,
          })}
        </Button>
      )}
    </div>
  );
});

ProfileHeader.displayName = "ProfileHeader";
